import {
  sectionViewAABBOnFrontPlane,
  sectionViewFrontPlane,
} from "building-generation/generationTools/sectionViewClippingPlane";
import type { Building } from "building-types/building/buildingTypes";
import { FaceCategory } from "building-types/materials/faceCategories";
import type { SheetGeometryList } from "building-types/views/sheetGeometryTypes";
import {
  sheetAddGeometry,
  sheetGeometryListNew,
  sheetPolylineGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import type { SectionViewGeometry } from "building-types/views/View3D";
import type { ViewFilter } from "building-types/views/viewFilterTypes";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import perfNow from "common/utils/perfNow";
import { DIST_EPSILON } from "geom/core";
import { intersectFacePlane } from "geom/intersect";
import { IntersectionKind } from "geom/intersectRay2";
import { line2ClipToAABB } from "geom/line2ClipToAABB";
import { polyLine2HasNoNaN } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { FaceGroupBSP } from "lib/house/faceGroupBSP";
import { collectBuildingFacesByCategory } from "lib/house/plans/collectBuildingFaces";
import { defaultView3DFilter } from "lib/house/render/viewFilterDefaults";

/**
 * This renders intersections between building geometry and section view front plane.
 * This is particularly useful for geometry that's orthogonal to the section view plane.
 * @returns 2D geometry generated from the intersections.
 */
export function renderSectionFrontPlaneIntersections(
  faces: FaceGroupBSP[],
  sectionView: SectionViewGeometry,
  mirror: boolean,
  mirrorTranslationX: number,
): PolyLine2[] {
  const sectionClippingPlane = sectionViewFrontPlane(sectionView, mirror, mirrorTranslationX);

  if (!sectionClippingPlane) {
    return [];
  }

  const startTime = perfNow();

  const sectionAabb = sectionViewAABBOnFrontPlane(sectionView, mirror, mirrorTranslationX);

  const pathsOnPlane: number[][] = [];

  let lastU: number | undefined = undefined;
  let lastV: number | undefined = undefined;

  for (const group of faces) {
    for (const face of group.faces) {
      const errs: string[] = [];
      const intersection = intersectFacePlane(face, sectionClippingPlane, errs);
      if (!intersection) {
        if (errs.length > 0) {
          console.error(errs);
        }
        continue;
      }
      if (intersection.kind === IntersectionKind.TANGENT) {
        continue;
      }
      for (const segment of intersection.segments) {
        const clippedSegment = line2ClipToAABB(
          segment.planeU0,
          segment.planeV0,
          segment.planeU1,
          segment.planeV1,
          sectionAabb,
        );
        if (!clippedSegment) {
          continue;
        }
        const [u0, v0, u1, v1] = clippedSegment;
        if (lastU !== undefined && lastV !== undefined) {
          if (Math.abs(u0 - lastU) < DIST_EPSILON && Math.abs(u0 - lastV) < DIST_EPSILON) {
            pathsOnPlane[pathsOnPlane.length - 1].push(u1, v1);
            lastU = u1;
            lastV = v1;
            continue;
          } else if (Math.abs(u1 - lastU) < DIST_EPSILON && Math.abs(v1 - lastV) < DIST_EPSILON) {
            pathsOnPlane[pathsOnPlane.length - 1].push(u0, v0);
            lastU = u0;
            lastV = v0;
            continue;
          }
        }
        pathsOnPlane.push([u0, v0, u1, v1]);
        lastU = u1;
        lastV = v1;
      }
    }
  }

  const endTime = perfNow();

  if (endTime - startTime > 100) {
    console.log(`Intersect building with front plane: ${(endTime - startTime).toFixed(1)}ms`);
  }

  return pathsOnPlane.map((path) => polyLine2New(path)).filter((path) => polyLine2HasNoNaN(path));
}

export function renderSectionFrontPlaneIntersectionsGeometry(
  building: Building,
  sectionView: SectionViewGeometry,
  viewFilter?: Partial<ViewFilter>,
): Table<SheetGeometryList> {
  const mirror = building.mirror;
  const mirrorTranslationX = building.mirrorTranslationX;

  const sectionClippingPlane = sectionViewFrontPlane(sectionView, mirror, mirrorTranslationX);

  if (!sectionClippingPlane) {
    return {};
  }

  const groupsByCategory = collectBuildingFacesByCategory({ building });

  const viewFilterToUse: ViewFilter | undefined = viewFilter ? { ...defaultView3DFilter(), ...viewFilter } : undefined;

  const sectionIntersections = {};
  for (const key in groupsByCategory) {
    if (parseInt(key) === FaceCategory.GROUND) {
      continue;
    }

    if (viewFilterToUse && !view3DFilterHasFaceCategory(viewFilterToUse, parseInt(key))) {
      continue;
    }

    const planGeometryList = sheetGeometryListNew();

    const polylines = sheetPolylineGeometryNew({
      paths: renderSectionFrontPlaneIntersections(groupsByCategory[key], sectionView, mirror, mirrorTranslationX),
    });
    sheetAddGeometry(planGeometryList, polylines);
    sectionIntersections[key] = planGeometryList;
  }

  return sectionIntersections;
}

function view3DFilterHasFaceCategory(viewFilter: ViewFilter, faceCategory: FaceCategory): boolean {
  // This is a hack to fit two different categorization systems together.
  // Note that some of the ViewFilter categories appear only on plan views, this should use those categories that are present on 3D views.
  switch (faceCategory) {
    case FaceCategory.CASEWORK:
      return viewFilter.casework;
    case FaceCategory.CEILING:
      return viewFilter.ceilings;
    case FaceCategory.DOOR_DECORATION:
    case FaceCategory.DOOR_EXTERIOR:
    case FaceCategory.DOOR_CURVED_WALL_EXTERIOR:
    case FaceCategory.DOOR_INTERIOR:
    case FaceCategory.DOOR_CURVED_WALL_INTERIOR:
      return viewFilter.doorUnits;
    case FaceCategory.CORNER_BOARD:
      return viewFilter.cornerBoards;
    case FaceCategory.FACADE_WALL_EXTERIOR:
      return viewFilter.walls;
    case FaceCategory.FLOOR:
      return viewFilter.flooring;
    case FaceCategory.FLOOR_FRAMING:
      return viewFilter.floorFraming;
    case FaceCategory.SUB_FLOORING:
      return viewFilter.subFlooring;
    case FaceCategory.SLAB_FLOORING:
      return viewFilter.slabFlooring;
    case FaceCategory.FOUNDATION_FOOTING:
      return viewFilter.foundationFooting;
    case FaceCategory.FOUNDATION_GARAGE_CURB:
      return viewFilter.foundationCurb;
    case FaceCategory.FOUNDATION_SLAB:
      return viewFilter.slab;
    case FaceCategory.FOUNDATION_STEM_WALL:
      return viewFilter.stemWall;
    case FaceCategory.FOUNDATION_PIER:
      return viewFilter.foundationPiers;
    case FaceCategory.GROUND:
      return false;
    case FaceCategory.PORCH_DECORATION:
    case FaceCategory.PORCH_EXTERIOR:
      return viewFilter.porches;
    case FaceCategory.PROP_APPLIANCES:
      return viewFilter.propAppliances;
    case FaceCategory.PROP_FIXTURES:
      return viewFilter.propFixtures;
    case FaceCategory.PROP_FURNITURE:
      return viewFilter.propFurniture;
    case FaceCategory.PROP_OTHER:
      return viewFilter.propOther;
    case FaceCategory.PROP_FOLIAGE:
      return false;
    case FaceCategory.RAILING:
      return viewFilter.railings;
    case FaceCategory.STAIR:
      return viewFilter.stairSystems;
    case FaceCategory.ROOF_BOTTOM:
    case FaceCategory.ROOF_DORMER_WALL:
      return viewFilter.roofs;
    case FaceCategory.ROOF_RIDGE_VENT:
      return viewFilter.roofRidgeVent;
    case FaceCategory.ROOF_RIDGE_CAP:
      return false;
    case FaceCategory.ROOF_TOP:
    case FaceCategory.ROOF_TOP_CUSTOMIZABLE_EXPORT_GEOMETRY:
    case FaceCategory.ROOF_FRIEZE_BOARD:
    case FaceCategory.ROOF_FASCIA_BOARD:
    case FaceCategory.ROOF_SOFFIT:
    case FaceCategory.ROOF_FASCIA_TOP_TRIM:
    case FaceCategory.ROOF_BELLY_BAND_BOARD:
    case FaceCategory.ROOF_WALL_BASE_BOARD:
    case FaceCategory.ROOF_EXPOSED_RAFTER:
    case FaceCategory.ROOF_GABLE_VENT:
    case FaceCategory.ROOF_WALL_EXTERIOR:
    case FaceCategory.ROOF_WALL_TRIM:
    case FaceCategory.ROOF_FRAMING:
      return viewFilter.roofs;
    case FaceCategory.ROOM_WALL_BREP:
    case FaceCategory.ROOM_WALL_EXTERIOR:
    case FaceCategory.ROOM_WALL_EXTRUSION:
    case FaceCategory.ROOM_WALL_INTERIOR:
      return viewFilter.walls;
    case FaceCategory.WAINSCOTING:
    case FaceCategory.WAINSCOTING_TRIM:
      return viewFilter.wainscoting;
    case FaceCategory.WALL_FRAMING:
    case FaceCategory.GABLE_END_WALL_FRAMING:
      return viewFilter.wallFraming;
    case FaceCategory.WINDOW_DECORATION:
    case FaceCategory.WINDOW_EXTERIOR:
    case FaceCategory.WINDOW_GRID:
    case FaceCategory.WINDOW_GLASS:
    case FaceCategory.WINDOW_INTERIOR:
    case FaceCategory.WINDOW_SCREEN:
      return viewFilter.windowUnits;
    case FaceCategory.BASE_TRIM_MOLDING:
      return viewFilter.baseTrimMolding;
    case FaceCategory.CROWN_TRIM_MOLDING:
      return viewFilter.crownTrimMolding;
    case FaceCategory.WALL_PLANE_TRIM_MOLDING:
      return viewFilter.interiorWallPlaneTrimMolding;
    case FaceCategory.COLUMN:
      return viewFilter.columns;
    case FaceCategory.SKYLIGHT_GLASS:
    case FaceCategory.SKYLIGHT_EXTERIOR:
    case FaceCategory.SKYLIGHT_DECORATION:
      return viewFilter.roofs;
    // Site geometry - not shown in section views
    case FaceCategory.SITE_ROAD:
    case FaceCategory.SITE_GRASS:
    case FaceCategory.SITE_SIDEWALK:
    case FaceCategory.SITE_CURB:
    case FaceCategory.SITE_DRIVEWAY:
    case FaceCategory.SITE_FENCE:
    case FaceCategory.SITE_TOPOGRAPHY:
    case FaceCategory.SITE_WHITEBOX:
    case FaceCategory.SITE_PLANE:
      return false;
    default:
      assertUnreachable(faceCategory);
  }
}
